import React, { useState, useEffect, useRef } from "react";
import { API, Auth, Hub } from "aws-amplify";
import {
  Route,
  Link,
  useRouteMatch,
  useHistory,
  Switch,
  Redirect,
  useParams,
} from "react-router-dom";

import '../../assets/_master.scss';

// --- Origin helpers (unify iframe src + postMessage target) ---
const normalizeOrigin = (v) => {
  if (!v) return null;
  return /^https?:\/\//i.test(v) ? v : `https://${v}`;
};

// Local dev uses localhost; otherwise use env-provided domain
const ENV = process.env.REACT_APP_ENV; // e.g., "local"
const QUERY_UI_DOMAIN = normalizeOrigin(process.env.REACT_APP_QUERY_UI_CLOUDFRONT_DOMAIN);

// Fail-fast placeholder if missing in non-local envs
const targetOrigin =
  ENV === "local"
    ? "http://localhost:8081"
    : (QUERY_UI_DOMAIN || "https://example.invalid");

function Report(props) {
  const history = useHistory();
  return (
    <div className="row">
      <div className="col-12 pb-3">
        <button
          type="button"
          className="close"
          aria-label="Close"
          onClick={() => history.push("/openquery")}
        >
          <span aria-hidden="true">&times;</span>
        </button>
      </div>
      <div className="col-12 embed-responsive embed-responsive-16by9">
        {/* Single source of truth for iframe src */}
        <iframe
          src={`${targetOrigin}/${props.reportId}/index.html`}
          title="Perception Health Report Request"
          className="embed-responsive-item"
        />
      </div>
    </div>
  );
}

async function getToken() {
  // Ensure Amplify session exists before any API call
  await Auth.currentAuthenticatedUser();
  try {
    const session = await Auth.currentSession();
    return session.getIdToken().getJwtToken();
  } catch (err) {
    console.error("error getting token", err);
  }
}

function OpenQueryContainer() {
  const [dynamicReportList, setDynamicReportList] = useState([]);
  const [ready, setReady] = useState(false); // auth/session ready

  const reportsInFlight = useRef(false);

  const history = useHistory();
  const { path, url } = useRouteMatch();
  const { payerMix } = useParams();

  // Warn in non-local if the domain is missing
  useEffect(() => {
    if (ENV !== "local" && !QUERY_UI_DOMAIN) {
      // Using example.invalid as a safe sink; devs will see this log
      console.error(
        "REACT_APP_QUERY_UI_CLOUDFRONT_DOMAIN is not set. Iframe will not load correctly."
      );
    }
  }, []);

  // --- Auth readiness ---
  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        await Auth.currentAuthenticatedUser();
        if (mounted) setReady(true);
      } catch {
        if (mounted) setReady(false);
      }
    })();

    const unsub = Hub.listen("auth", async ({ payload }) => {
      const evt = payload?.event;
      if (evt === "signIn" || evt === "cognitoHostedUI") {
        try {
          await Auth.currentAuthenticatedUser();
          if (mounted) setReady(true);
        } catch {
          if (mounted) setReady(false);
        }
      } else if (evt === "signOut") {
        if (mounted) {
          setReady(false);
          setDynamicReportList([]);
        }
      }
    });

    return () => {
      mounted = false;
      unsub();
    };
  }, []);

  // --- Fetch reports only when authenticated ---
  useEffect(() => {
    if (!ready) return;
    if (reportsInFlight.current) return;
    reportsInFlight.current = true;

    let cancelled = false;

    (async () => {
      try {
        await Auth.currentAuthenticatedUser(); // double-check
        const reports = await API.get("OpenQueryAPI", `/openquery/reports`);
        if (cancelled) return;

        // Include ".Combo" => "commercial-and-medicare"
        const shapedReports = reports.reports.category.reduce(
          (tabsArr, category) => {
            const categoryContents = category.reports.reduce(
              (reportSet, report) => {
                if (report.name.includes(".Medicare")) {
                  reportSet.add("medicare");
                } else if (report.name.includes(".Combo")) {
                  reportSet.add("commercial-and-medicare");
                } else {
                  reportSet.add("commercial");
                }
                return reportSet;
              },
              new Set()
            );
            categoryContents.forEach((cat) => {
              tabsArr.push({
                name: category.name,
                description: category.description,
                id: category.id,
                type: cat,
              });
            });
            return tabsArr;
          },
          []
        );

        setDynamicReportList(shapedReports);
      } catch (err) {
        console.error("error fetching openquery reports", err);
      } finally {
        reportsInFlight.current = false;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [ready]);

  // --- Iframe messaging (token + payer mix + PROXY for /openquery/jobs) ---
  useEffect(() => {
    const handleIframeMessage = (e) => {
      // Only accept messages from the iframe origin we expect
      if (!e || e.origin !== targetOrigin) {
        return;
      }

      const iframeWindow = document.querySelector("iframe")?.contentWindow;
      if (!iframeWindow) {
        // no iframe visible right now; nothing to respond to
        return;
      }

      const sendToken = async () => {
        try {
          const token = await getToken();
          // send structured token message (new style)
          iframeWindow.postMessage({ type: "TOKEN", token }, targetOrigin);
        } catch {
          // swallow
        }
      };

      const sendPayerMix = (mix) => {
        iframeWindow.postMessage({ payerMix: mix }, targetOrigin);
      };

      // --- Legacy string-based messages (existing behavior) ---
      if (typeof e.data === "string") {
        if (e.data === "TOKEN_REQUEST") {
          sendToken();
        } else if (e.data === "QUERY_SUCCESSFULLY_SUBMITTED") {
          history.push("/openquery");
        } else if (e.data === "GET_PAYER_MIX") {
          sendPayerMix(payerMix);
        }
        return;
      }

      // --- New structured messages ---
      const { type } = e.data || {};

      if (type === "TOKEN_REQUEST") {
        sendToken();
      } else if (type === "GET_PAYER_MIX") {
        sendPayerMix(payerMix);
      } else if (type === "QUERY_SUCCESSFULLY_SUBMITTED") {
        history.push("/openquery");
      } else if (type === "OPENQUERY_START_JOB") {
        // ***** PROXY CALL: parent invokes /openquery/jobs, NOT the iframe *****
        const { categoryId, definition } = e.data || {};

        if (!definition) {
          console.error("OPENQUERY_START_JOB missing definition:", e.data);
          return;
        }

        (async () => {
          try {
            await Auth.currentAuthenticatedUser();
            const response = await API.post(
              "OpenQueryAPI",
              "/openquery/jobs",
              { body: definition } // definition is the queryObj from iframe
            );

            // Try to infer HTTP status if present, else default to 200
            const status =
              (response && response.statusCode) ||
              (response && response.status) ||
              200;

            iframeWindow.postMessage(
              {
                type: "OPENQUERY_START_JOB_RESULT",
                ok: true,
                status,
                body: response,
                categoryId,
              },
              targetOrigin
            );
          } catch (err) {
            console.error("error submitting openquery job from parent", err);

            const status =
              err?.response?.status ||
              err?.response?.statusCode ||
              500;

            const message =
              err?.response?.data?.message ||
              err?.message ||
              "Job submit failed";

            iframeWindow.postMessage(
              {
                type: "OPENQUERY_START_JOB_RESULT",
                ok: false,
                status,
                body: { message },
                categoryId,
              },
              targetOrigin
            );
          }
        })();
      }
    };

    window.addEventListener("message", handleIframeMessage);
    return () => window.removeEventListener("message", handleIframeMessage);
  }, [history, payerMix]);

  return (
    <>
      {/* Allow the combined tab */}
      {["commercial", "medicare", "commercial-and-medicare"].indexOf(payerMix) === -1 ? (
        <Redirect to="/openquery/commercial" />
      ) : null}
      <Route exact path="/openquery">
        <Redirect to="/openquery/commercial" />
      </Route>
      <div className="card">
        <div
          className="card-header text-light font-weight-bold container-fluid"
        >
          <ul className="nav nav-tabs card-header-tabs">
            <li className="nav-item" style={{ cursor: "pointer" }}>
              <div className={payerMix === "commercial" ? "nav-link active" : "nav-link"}>
                <Link
                  to="/openquery/commercial"
                  style={{ color: payerMix === "commercial" ? "#000" : "#fff" }}
                >
                  Commercial
                </Link>
              </div>
            </li>
            <li className="nav-item" style={{ cursor: "pointer" }}>
              <div className={payerMix === "medicare" ? "nav-link active" : "nav-link"}>
                <Link
                  to="/openquery/medicare"
                  style={{ color: payerMix === "medicare" ? "#000" : "#fff" }}
                >
                  Medicare
                </Link>
              </div>
            </li>
            {/* Third tab for combined */}
            <li className="nav-item" style={{ cursor: "pointer" }}>
              <div
                className={
                  payerMix === "commercial-and-medicare" ? "nav-link active" : "nav-link"
                }
              >
                <Link
                  to="/openquery/commercial-and-medicare"
                  style={{
                    color: payerMix === "commercial-and-medicare" ? "#000" : "#fff",
                  }}
                >
                  Commercial + Medicare
                </Link>
              </div>
            </li>
          </ul>
        </div>
        <div>
          <div className="row">
            <div className="col-3">
              <p className="pl-3 pt-3 mb-0" >
                <b>Export Type</b>
              </p>
            </div>
            <div className="col-9">
              <p className="pl-3 pt-3 mb-0">
                <b>Export Description</b>
              </p>
            </div>
          </div>
          <hr />
          <ul className="list-group-flush container-fluid" style={{ padding: 0 }}>
            {dynamicReportList
              .filter((item) => payerMix === item.type)
              .map((item) => (
                <li key={item.name} className="list-group-item">
                  <div className="row">
                    <div className="col-3">
                      <Switch>
                        <Route path={`${path}/${item.id}`}>
                          <Link to={`/openquery/${payerMix}`}>
                            <p className="font-weight-bold">{item.name}</p>
                          </Link>
                        </Route>

                        <Route path="/openquery">
                          <Link to={`${url}/${item.id}`}>
                            <p className="font-weight-bold">{item.name}</p>
                          </Link>
                        </Route>
                      </Switch>
                    </div>
                    <div className="col-9">
                      <p>{item.description}</p>
                    </div>
                  </div>
                  <Route path={`${path}/${item.id}`}>
                    <Report reportId={item.id} />
                  </Route>
                </li>
              ))}
          </ul>
        </div>
      </div>
    </>
  );
}

export default OpenQueryContainer;
